import { format } from "date-fns";
import { MdDownload } from "react-icons/md";
import { SiMicrosoftexcel } from "react-icons/si";
import { FaRegFilePdf } from "react-icons/fa6";
import { Button } from "components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { Separator } from "components/ui/separator";
import { toast } from "components/ui/use-toast";
import { downloadExcel } from "@/utils/download-excel";
import { downloadPdf } from "@/utils/download-pdf";
import {
  useExportLackExcelMutation,
  useExportLackPdfMutation,
} from "@/services/lack-transactions";
import {
  useExportStatusGridExcelMutation,
  useExportStatusGridPdfMutation,
} from "@/services/conciliation-status-grid";
import type { ConciliationReportViewType, DateRangeType } from "../../types";
import { useFilterState } from "../../ducks/filter/hooks/filterState";

interface ExportOptionsProps {
  activeTab: ConciliationReportViewType;
  dateRange: DateRangeType | undefined;
}

const exportButtonClass =
  "w-full flex items-center gap-3 px-3 py-3 text-sm text-left hover:bg-[#F4C83E]/20 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 group";

const iconContainerClass =
  "flex items-center justify-center w-8 h-8 bg-[#F4C83E]/20 rounded-lg group-hover:bg-[#F4C83E]/30 transition-colors";

export default function ExportOptions({ activeTab, dateRange }: ExportOptionsProps) {
  const filterState = useFilterState();
  const { filters } = filterState || { filters: {} };

  const [exportLackExcel, { isLoading: isExportingLackExcel }] = useExportLackExcelMutation();
  const [exportLackPdf, { isLoading: isExportingLackPdf }] = useExportLackPdfMutation();
  const [exportGridExcel, { isLoading: isExportingGridExcel }] = useExportStatusGridExcelMutation();
  const [exportGridPdf, { isLoading: isExportingGridPdf }] = useExportStatusGridPdfMutation();

  const isExporting = isExportingLackExcel || isExportingLackPdf || isExportingGridExcel || isExportingGridPdf;

  const startDate = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : "";
  const endDate = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : "";
  const hasDateRange = !!startDate && !!endDate;

  // Filtros da aba "lack" (SelectFilters)
  const lackFilters = {
    ...(filters.code ? { code: String(filters.code) } : {}),
    ...(filters.responsible ? { responsibleId: String(filters.responsible) } : {}),
    ...(filters.regional_id ? { regionalId: String(filters.regional_id) } : {}),
  };

  // Filtros da aba "status-grid" (GridFilters)
  const gridFilters = {
    ...(filters.storeCode ? { storeCode: String(filters.storeCode) } : {}),
    ...(filters.regionalId ? { regionalId: String(filters.regionalId) } : {}),
    ...(filters.responsibleId ? { responsibleId: String(filters.responsibleId) } : {}),
  };

  const handleExport = async (fn: () => Promise<void>) => {
    if (!hasDateRange) return;
    try {
      await fn();
    } catch {
      toast({ description: "Erro ao exportar relatório.", variant: "destructive" });
    }
  };

  const handleLackExcel = () =>
    handleExport(async () => {
      const result = await exportLackExcel({ reportType: "lack", startDate, endDate, ...lackFilters }).unwrap();
      downloadExcel(result, `falta-conciliacao-${startDate}-a-${endDate}.xlsx`);
    });

  const handleLackPdf = () =>
    handleExport(async () => {
      const result = await exportLackPdf({ reportType: "lack", startDate, endDate, ...lackFilters }).unwrap();
      downloadPdf(result, `falta-conciliacao-${startDate}-a-${endDate}.pdf`);
    });

  const handleGridExcel = () =>
    handleExport(async () => {
      const result = await exportGridExcel({ startDate, endDate, ...gridFilters }).unwrap();
      downloadExcel(result, `grade-status-${startDate}-a-${endDate}.xlsx`);
    });

  const handleGridPdf = () =>
    handleExport(async () => {
      const result = await exportGridPdf({ startDate, endDate, ...gridFilters }).unwrap();
      downloadPdf(result, `grade-status-${startDate}-a-${endDate}.pdf`);
    });

  return (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2 text-sm font-medium text-[#2B0C1A]">
        <span>Exportar Dados:</span>
      </div>

      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={isExporting || !hasDateRange}
            className="flex items-center gap-2 min-w-[160px] transition-all duration-200"
          >
            <MdDownload className="w-4 h-4" />
            {isExporting ? "Exportando..." : "Escolher Formato"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3 bg-white shadow-lg border">
          <div className="space-y-1">
            <div className="px-2 py-1 text-xs font-medium text-[#2B0C1A] uppercase tracking-wide">
              Formatos Disponíveis
            </div>

            {activeTab === "lack" ? (
              <>
                <button onClick={handleLackExcel} disabled={isExporting} className={exportButtonClass}>
                  <div className={iconContainerClass}>
                    <SiMicrosoftexcel className="w-4 h-4 text-[#2B0C1A]" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-[#2B0C1A]">Planilha Excel</div>
                    <div className="text-xs text-gray-600">Exportar divergências em .xlsx</div>
                  </div>
                </button>

                <Separator className="my-2 bg-[#2B0C1A]/20" />

                <button onClick={handleLackPdf} disabled={isExporting} className={exportButtonClass}>
                  <div className={iconContainerClass}>
                    <FaRegFilePdf className="w-4 h-4 text-[#2B0C1A]" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-[#2B0C1A]">Relatório PDF</div>
                    <div className="text-xs text-gray-600">Exportar divergências para impressão</div>
                  </div>
                </button>
              </>
            ) : (
              <>
                <button onClick={handleGridExcel} disabled={isExporting} className={exportButtonClass}>
                  <div className={iconContainerClass}>
                    <SiMicrosoftexcel className="w-4 h-4 text-[#2B0C1A]" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-[#2B0C1A]">Planilha Excel</div>
                    <div className="text-xs text-gray-600">Exportar grade de status em .xlsx</div>
                  </div>
                </button>

                <Separator className="my-2 bg-[#2B0C1A]/20" />

                <button onClick={handleGridPdf} disabled={isExporting} className={exportButtonClass}>
                  <div className={iconContainerClass}>
                    <FaRegFilePdf className="w-4 h-4 text-[#2B0C1A]" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-[#2B0C1A]">Relatório PDF</div>
                    <div className="text-xs text-gray-600">Exportar grade de status para impressão</div>
                  </div>
                </button>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
